import { useCallback, useEffect, useRef, useState } from "react";

const MAX_RECONNECT_ATTEMPTS = 5;
/** How often `{"action":"status"}` goes out while waiting for the intro frame. */
const STATUS_POLL_INTERVAL = 10000;
const RECONNECT_DECAY = 1.5;
const MAX_RECONNECT_INTERVAL = 30000;

/** `mode` decides how much the backend exposes — student frames hide the answers. */
export const LowerSchoolStoryMode = {
  PREVIEW: "preview",
  STUDENT: "student",
};

/** Frames the backend pushes down the socket. */
export const LowerSchoolStoryFrame = {
  /** The title card is still being built — keep polling `status`. */
  INTRO_GENERATING: "intro_generating",
  INTRO: "intro",
  GENERATING: "generating",
  NODE: "node",
  STORY_END: "story_end",
  /** Reply to `{"action":"submit"}` — the attempt is in, with its score. */
  COMPLETED: "completed",
  /**
   * Follows `completed` once the backend has written the child's feedback and
   * narrated it: this is the frame the completion screen is built from
   * (`completion_html`, `completion_audio_url`, `child_message`).
   */
  FEEDBACK: "feedback",
  /** `{type:"error", code, message}` — e.g. `generation_failed`. */
  ERROR: "error",
};

const GENERIC_ERROR_MESSAGE = "Something went wrong, please try again later!";

/** What the UI should be rendering right now. */
export const LowerSchoolStoryStatus = {
  IDLE: "idle",
  INTRO_GENERATING: "intro_generating",
  INTRO: "intro",
  GENERATING: "generating",
  NODE: "node",
  ENDED: "ended",
  COMPLETED: "completed",
};

/**
 * Drives one Lower School story session over
 * `/ws/lower_school/<room_id>/?lesson_id=…&mode=…&token=…`.
 *
 * The same hook backs both surfaces — only `mode` changes:
 *   • teacher Student Preview → `LowerSchoolStoryMode.PREVIEW`
 *   • student attempt         → `LowerSchoolStoryMode.STUDENT`
 *
 * Flow: connect → `intro` (title card) → {"action":"start"} → `generating` →
 * `node` → {"action":"answer"} → … → `story_end` → {"action":"submit"} →
 * `completed` → `feedback` (the completion screen itself, once the child's
 * feedback has been written and narrated).
 *
 * Student-mode `node` frames carry only `id`, `text` and `image_url` per option;
 * `is_correct` / `outcome` / `image_prompt` are stripped server side so the
 * answer never reaches the browser.
 */
export const useLowerSchoolStorySocket = ({
  wsURL,
  token,
  /** Chat room the lesson belongs to — the `room_id` from the lesson session. */
  roomId,
  lessonId,
  mode = LowerSchoolStoryMode.PREVIEW,
  /** Keeps the socket closed until the surface is actually visible. */
  enabled = true,
  onIntro,
  onGenerating,
  onNode,
  onStoryEnd,
  onCompleted,
  onFeedback,
  onError,
}) => {
  const socketRef = useRef(null);
  const shouldReconnectRef = useRef(true);
  const reconnectAttemptsRef = useRef(0);
  const reconnectIntervalRef = useRef(1000);
  const reconnectTimerRef = useRef(null);
  const statusPollTimerRef = useRef(null);
  /** Polling only runs until the first `intro` frame. */
  const hasIntroRef = useRef(false);

  const [isSocketReady, setIsSocketReady] = useState(false);
  const [status, setStatus] = useState(LowerSchoolStoryStatus.IDLE);
  const [intro, setIntro] = useState(null);
  const [generating, setGenerating] = useState(null);
  const [node, setNode] = useState(null);
  const [storyEnd, setStoryEnd] = useState(null);
  const [completed, setCompleted] = useState(null);
  /** The completion screen — see `LowerSchoolStoryFrame.FEEDBACK`. */
  const [feedback, setFeedback] = useState(null);
  const [socketError, setSocketError] = useState("");

  // Handlers live in a ref so socket callbacks always see the latest closures
  // without tearing the connection down on every parent re-render.
  const handlersRef = useRef({
    onIntro,
    onGenerating,
    onNode,
    onStoryEnd,
    onCompleted,
    onFeedback,
    onError,
  });
  useEffect(() => {
    handlersRef.current = {
      onIntro,
      onGenerating,
      onNode,
      onStoryEnd,
      onCompleted,
      onFeedback,
      onError,
    };
  }, [
    onIntro,
    onGenerating,
    onNode,
    onStoryEnd,
    onCompleted,
    onFeedback,
    onError,
  ]);

  const sendStatusAction = useCallback(() => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    socket.send(JSON.stringify({ action: "status" }));
  }, []);

  const stopStatusPolling = useCallback(() => {
    if (statusPollTimerRef.current) {
      clearInterval(statusPollTimerRef.current);
      statusPollTimerRef.current = null;
    }
  }, []);

  /**
   * The title card can still be generating when we connect, so `status` goes out
   * immediately and then every 10s. `intro_generating` replies mean "still
   * building" — keep asking. Polling ends when the `intro` frame arrives with the
   * theme html and lesson image, or when the socket closes with the modal.
   */
  const startStatusPolling = useCallback(() => {
    if (hasIntroRef.current) return;
    sendStatusAction();
    if (statusPollTimerRef.current) return;
    statusPollTimerRef.current = setInterval(
      sendStatusAction,
      STATUS_POLL_INTERVAL
    );
  }, [sendStatusAction]);

  const handleMessage = useCallback((event) => {
    let data;
    try {
      data = JSON.parse(event.data);
    } catch (error) {
      console.error("Lower school story socket: unparseable frame", error);
      return;
    }

    // Error frames end the current step but leave the socket usable.
    // `{type:"error", code, message}` (e.g. `generation_failed`) gets a generic
    // message — the backend text isn't meant for the child/teacher.
    if (data?.type === LowerSchoolStoryFrame.ERROR) {
      console.error("Lower school story socket error frame:", data);
      setGenerating(null);
      setSocketError(GENERIC_ERROR_MESSAGE);
      handlersRef.current.onError?.(data);
      return;
    }

    if (data?.error) {
      console.error("Lower school story socket error frame:", data);
      const message = data.detail ? `${data.error} ${data.detail}` : data.error;
      setSocketError(message);
      handlersRef.current.onError?.(data);
      return;
    }

    switch (data?.type) {
      case LowerSchoolStoryFrame.INTRO_GENERATING:
        // Still building the title card — the poll keeps running.
        setStatus(LowerSchoolStoryStatus.INTRO_GENERATING);
        break;

      case LowerSchoolStoryFrame.INTRO:
        // The intro is here — nothing left to poll for.
        hasIntroRef.current = true;
        stopStatusPolling();
        setIntro(data);
        setGenerating(null);
        setNode(null);
        setStoryEnd(null);
        setCompleted(null);
        setFeedback(null);
        setStatus(LowerSchoolStoryStatus.INTRO);
        handlersRef.current.onIntro?.(data);
        break;

      case LowerSchoolStoryFrame.GENERATING:
        setGenerating(data);
        setStatus(LowerSchoolStoryStatus.GENERATING);
        handlersRef.current.onGenerating?.(data);
        break;

      case LowerSchoolStoryFrame.NODE: {
        // The screen itself sits under `node`, while `question_id` / `progress` /
        // `mode` ride on the frame — flatten so consumers read one object.
        const screen = data.node ?? data;
        const flattened = {
          ...screen,
          // `null` on an advance-only screen (one option, no question) — the
          // answer then goes out without a `question_id`.
          question_id: data.question_id ?? screen.question_id ?? null,
          advance_only: data.advance_only ?? screen.advance_only ?? false,
          progress: data.progress ?? screen.progress ?? {},
          mode: data.mode ?? screen.mode,
          // Set when revisiting a screen the student already answered — the
          // frontend re-highlights `student_answer_id`.
          answered: data.answered ?? screen.answered ?? false,
          student_answer_id:
            data.student_answer_id ?? screen.student_answer_id ?? null,
        };
        setNode(flattened);
        setGenerating(null);
        setStatus(LowerSchoolStoryStatus.NODE);
        handlersRef.current.onNode?.(flattened);
        break;
      }

      case LowerSchoolStoryFrame.STORY_END:
        setStoryEnd(data);
        setGenerating(null);
        setStatus(LowerSchoolStoryStatus.ENDED);
        handlersRef.current.onStoryEnd?.(data);
        break;

      case LowerSchoolStoryFrame.COMPLETED:
        // The submission landed — the attempt number and the score ride on it.
        // The screen the student finishes on comes separately, on `feedback`.
        setCompleted(data);
        setGenerating(null);
        setStatus(LowerSchoolStoryStatus.COMPLETED);
        handlersRef.current.onCompleted?.(data);
        break;

      case LowerSchoolStoryFrame.FEEDBACK:
        // The completion screen itself: `completion_html` plus the clip that
        // reads it out. Writing and narrating the child's feedback takes the
        // backend a moment after `completed`, so `feedback_status` can arrive
        // as something other than "completed" — the markup is what says the
        // screen is ready, so consumers key off that rather than the status.
        setFeedback(data);
        setGenerating(null);
        if (data?.completion_html) {
          setStatus(LowerSchoolStoryStatus.COMPLETED);
        }
        handlersRef.current.onFeedback?.(data);
        break;

      default:
        // Unknown frame types are ignored so a backend addition can't break the UI.
        break;
    }
  }, []);

  // ── connection (opens while `enabled`, reconnects with backoff) ────────────
  useEffect(() => {
    if (!enabled || !token || !lessonId || !roomId) return undefined;

    shouldReconnectRef.current = true;

    const connect = () => {
      const url =
        `${wsURL}ws/lower_school/${roomId}/` +
        `?lesson_id=${encodeURIComponent(lessonId)}` +
        `&mode=${encodeURIComponent(mode)}&token=${token}`;
      const socket = new WebSocket(url);
      socketRef.current = socket;

      socket.onopen = () => {
        setIsSocketReady(true);
        setSocketError("");
        reconnectAttemptsRef.current = 0;
        reconnectIntervalRef.current = 1000;
        // No-op once the intro has already been seen.
        startStatusPolling();
      };

      socket.onmessage = handleMessage;

      socket.onclose = () => {
        setIsSocketReady(false);
        stopStatusPolling();
        if (
          !shouldReconnectRef.current ||
          reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS
        ) {
          return;
        }
        reconnectTimerRef.current = setTimeout(() => {
          reconnectAttemptsRef.current += 1;
          reconnectIntervalRef.current = Math.min(
            reconnectIntervalRef.current * RECONNECT_DECAY,
            MAX_RECONNECT_INTERVAL
          );
          connect();
        }, reconnectIntervalRef.current);
      };

      socket.onerror = (error) => {
        console.error("Lower school story socket error:", error);
        socket.close();
      };
    };

    connect();

    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      // Closing the modal ends the polling with the socket.
      stopStatusPolling();
      hasIntroRef.current = false;
      const socket = socketRef.current;
      if (
        socket &&
        (socket.readyState === WebSocket.OPEN ||
          socket.readyState === WebSocket.CONNECTING)
      ) {
        socket.close();
      }
      socketRef.current = null;
      setIsSocketReady(false);
      // A closed session starts over from the intro next time it opens.
      setStatus(LowerSchoolStoryStatus.IDLE);
      setIntro(null);
      setGenerating(null);
      setNode(null);
      setStoryEnd(null);
      setCompleted(null);
      setFeedback(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, wsURL, token, roomId, lessonId, mode]);

  /**
   * Sends one action frame.
   * @returns {boolean} false when the socket is not connected
   */
  const sendAction = useCallback((payload) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      setSocketError("Please try again after sometime.");
      return false;
    }
    setSocketError("");
    socket.send(JSON.stringify(payload));
    return true;
  }, []);

  /** "Start The Story" — the backend replies with `generating` then `node`. */
  const startStory = useCallback(() => sendAction({ action: "start" }), [
    sendAction,
  ]);

  /**
   * Advance-only screens arrive with `question_id: null` — the backend then
   * expects just `{"action":"answer","option_id":…}`, so the key is left out.
   */
  const answerQuestion = useCallback(
    (questionId, optionId) =>
      sendAction({
        action: "answer",
        ...(questionId != null && { question_id: questionId }),
        option_id: optionId,
      }),
    [sendAction]
  );

  /** "Back" — the backend replies with the previous screen, same as any node. */
  const goBack = useCallback(() => sendAction({ action: "back" }), [sendAction]);

  /** Only valid once `story_end` has arrived. */
  const submitStory = useCallback(() => sendAction({ action: "submit" }), [
    sendAction,
  ]);

  return {
    // state
    status,
    intro,
    generating,
    node,
    storyEnd,
    completed,
    feedback,
    isSocketReady,
    socketError,
    setSocketError,
    // actions
    startStory,
    answerQuestion,
    goBack,
    submitStory,
  };
};

export default useLowerSchoolStorySocket;
